// react
import { useCallback } from 'react';

// hooks
import { useCareProfile } from 'hooks';
import { useGoBack } from 'hooks/useGoBack';

// store
import { useSelector, dispatch } from 'store';
import { getAccount } from 'store/slices/iamSlice';
import { newToast } from 'store/slices/uiSlice';

// router
import { ROUTES } from 'router';

// components
import { FormIntroPage } from '@karehero/llama/pwa/pages/FormIntroPage/FormIntroPage';
import { Keni } from '@karehero/llama/components/atoms/Keni/Keni';
import { CareProfileCategoryID } from '@karehero/models';

// mui
import { Box } from '@mui/material';

/**
 * CareProfileStart is the dashboard page.
 */
export const CareProfileStart = () => {
  // hooks
  const goBack = useGoBack();
  const { handleStart, careProfileID } = useCareProfile({
    categoryID: CareProfileCategoryID.CareAssessment,
  });
  const account = useSelector(getAccount);
  const isAdminWithoutCareProfile =
    account?.accountRole?.name === 'KareHero Administrator' && !careProfileID;

  const onStart = useCallback(() => {
    if (isAdminWithoutCareProfile) {
      dispatch(
        newToast({
          title: 'Please select a care circle first',
          variant: 'warning',
          action: {
            label: 'Select care circle',
            href: ROUTES.ADMIN_CARE_CIRCLE_MANAGE,
          },
        }),
      );
      return;
    }
    handleStart();
  }, [isAdminWithoutCareProfile, handleStart]);

  return (
    <FormIntroPage
      onBack={goBack}
      title='Create your personalised care assessment'
      description="You are about to start creating your personalised care assessment. As you go through you might feel there are a lot of questions to answer. Try to answer as best as you can. The more information that you can provide us with, the better your care assessment will be and the easier your care journey will become. We estimate the care assessment will take around 30 minutes to complete. If you feel you would benefit from our care experts' support, don't hesitate to get in touch."
      items={[
        {
          icon: 'check-circle',
          text: 'Answer questions about the care needs of your loved one',
        },
        {
          icon: 'user',
          text: 'Chat to your Care Expert regarding your Care Assessment',
        },
        {
          icon: 'file-contract',
          text: 'Receive a care search shortlist tailored by your Care Assessment',
        },
      ]}
      contentSlot={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              maxWidth: 300,
            }}
          >
            <Keni />
          </Box>
        </Box>
      }
      onStart={onStart}
    />
  );
};

export default CareProfileStart;
